import { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
    runOnJS,
    useAnimatedReaction,
    useSharedValue,
    withSpring,
    withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';

import { CloseIcon } from '@/assets/icons/close';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Image } from '@/components/lib/components';
import { Box } from '@/components/lib/components/Box';
import { Row } from '@/components/lib/components/Row';
import { TouchableBox } from '@/components/lib/components/TouchableBox';
import { ds } from '@/components/lib/design.system';
import { textVariants } from '@/components/lib/typography';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common } from '@/styles/styles';
import { isIos } from '@/utils/constants-platform-specific';
import { getAppVersion } from '@/utils/get-app-version';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';

import { WhatsNewCard } from '../components/WhatsNewCard';
import { useWhatsNewData } from '../hooks/use-whats-new-data';
import { useWhatsNewStore } from '../hooks/use-whats-new-store';
import { isCardVisible } from '../hooks/use-whats-new-visibility';
import {
    CARDS_LEFT_TEXT_ANIMATION_CONFIG,
    SNAP_BACK_ANIMATION_CONFIG,
    SWIPE_VELOCITY_THRESHOLD,
    TRANSLATE_X_THRESHOLD,
    TRANSLATE_X_THRESHOLD_RATIO,
    WHATS_NEW_CARD_HEIGHT,
    whatsNewEnteringKeyframe,
} from '../utils/whats-new-animations-utils';

export const WhatsNewScreen = () => {
    const navigation = useNavigation();
    const width = useWindowDimensions().width;

    const { data, isLoading } = useWhatsNewData();
    const markCardSeen = useWhatsNewStore(state => state.actions.markCardSeen);
    const markCardsSeen = useWhatsNewStore(state => state.actions.markCardsSeen);

    // getState() avoids subscribing to seenCardIds so cardsData stays stable
    // frontCardIndex is an absolute index
    // if the list shrinks while the index increments, we can jump ahead and skip multiple cards (which is wrong)
    const cardsData = useMemo(() => {
        if (!data) return [];
        const seenIds = useWhatsNewStore.getState().seenCardIds;
        const currentVersion = getAppVersion();
        return data.cards.filter(card => isCardVisible(card, seenIds, currentVersion));
    }, [data]);

    const [frontCardIndex, setFrontCardIndex] = useState(0);
    const frontCardData = cardsData[frontCardIndex];
    const cardsLeft = cardsData.length - frontCardIndex;

    const translateX = useSharedValue(0);
    const translateY = useSharedValue(0);
    const animatedActiveIndex = useSharedValue(0);

    const onSwipeComplete = useCallback(() => {
        if (frontCardData?.id) {
            markCardSeen(frontCardData.id);
        }
        setFrontCardIndex(prev => prev + 1);
    }, [frontCardData, markCardSeen]);

    useAnimatedReaction(
        () => Math.abs(translateX.value) >= TRANSLATE_X_THRESHOLD,
        (isPastThreshold, wasPastThreshold) => {
            if (isPastThreshold && !wasPastThreshold) {
                runOnJS(triggerHapticFeedback)();
            }
        }
    );

    const onSkip = useCallback(() => {
        markCardsSeen(cardsData.map(c => c.id));

        navigation.goBack();
    }, [navigation, markCardsSeen, cardsData]);

    const pan = useMemo(() => {
        return Gesture.Pan()
            .activeOffsetX([-5, 5])
            .onUpdate(e => {
                translateX.value = e.translationX;
                translateY.value = e.translationY;
            })
            .onEnd(e => {
                const translationThreshold = width * TRANSLATE_X_THRESHOLD_RATIO;
                const belowTranslationThreshold = Math.abs(e.translationX) < translationThreshold;
                const belowVelocityThreshold = Math.abs(e.velocityX) < SWIPE_VELOCITY_THRESHOLD;
                // if the translation and the swipe velocity are less than the threshold, snap back to the center
                if (belowVelocityThreshold && belowTranslationThreshold) {
                    translateX.value = withSpring(0, SNAP_BACK_ANIMATION_CONFIG);
                    translateY.value = withSpring(0, SNAP_BACK_ANIMATION_CONFIG);
                    return;
                }

                if (belowTranslationThreshold) {
                    // also trigger the haptic feedback on a fast swipe
                    runOnJS(triggerHapticFeedback)();
                }

                const direction = Math.sign(e.translationX || e.velocityX);
                const target = direction * 2 * width;
                const remaining = Math.abs(target - translateX.value);
                // exit based on velocity but max 250ms
                const duration = Math.min(250, (remaining / Math.abs(e.velocityX)) * 1000);

                translateX.value = withTiming(target, { duration }, isFinished => {
                    if (isFinished) {
                        animatedActiveIndex.value = animatedActiveIndex.value + 1;
                        translateX.value = 0;
                        translateY.value = 0;
                        runOnJS(onSwipeComplete)();
                    }
                });
            });
    }, [animatedActiveIndex, onSwipeComplete, translateX, translateY, width]);

    const visibleCards = useMemo(() => {
        return cardsData
            .slice(frontCardIndex, frontCardIndex + 4)
            .map((card, i) => ({ card, index: frontCardIndex + i }))
            .reverse();
    }, [cardsData, frontCardIndex]);

    return (
        <Box flex={1}>
            <Image
                source={require('@/assets/images/entry_confirmation_background.png')}
                style={StyleSheet.absoluteFill}
                resizeMode={'cover'}
            />
            <SafeAreaView style={common.flex}>
                <MaxWidthWrapper style={common.flex}>
                    <Row mt={'s6'} px={'s24'}>
                        <TouchableBox style={styles.close} onPress={() => navigation.goBack()}>
                            <CloseIcon width={20} height={20} color={ds.colorValue.icon.primary} />
                        </TouchableBox>
                        {!isLoading && cardsLeft > 0 ? (
                            <Box
                                flex={1}
                                style={StyleSheet.absoluteFill}
                                justifyContent={'center'}
                                pointerEvents={'none'}
                                alignItems={'center'}
                            >
                                <Animated.Text
                                    style={styles.cardsLeftText}
                                    entering={isIos ? CARDS_LEFT_TEXT_ANIMATION_CONFIG : undefined}
                                >{`${cardsLeft} left`}</Animated.Text>
                            </Box>
                        ) : null}
                    </Row>
                    <Box flex={1} px={'s24'}>
                        {isLoading ? (
                            <Box flex={1} justifyContent={'center'} alignItems={'center'}>
                                <ActivityIndicator color={ds.colorValue.icon.primary} />
                            </Box>
                        ) : cardsLeft > 0 ? (
                            <Animated.View style={common.flex} entering={isIos ? whatsNewEnteringKeyframe : undefined}>
                                <Box mt={'s32'} alignItems={'center'}>
                                    <Text variant={'headlineLarge'} color={ds.colorToken.text.primary}>
                                        {data?.page_title}
                                    </Text>
                                    <Text variant={'bodyMedium'} color={ds.colorToken.text.primary} opacity={0.7}>
                                        {data?.page_description}
                                    </Text>
                                </Box>

                                <Box flex={1} mt={'s56'}>
                                    <GestureDetector gesture={pan}>
                                        <Box height={WHATS_NEW_CARD_HEIGHT}>
                                            {visibleCards.map(({ card, index }) => {
                                                return (
                                                    <WhatsNewCard
                                                        key={index}
                                                        index={index}
                                                        animatedActiveIndex={animatedActiveIndex}
                                                        translateX={translateX}
                                                        translateY={translateY}
                                                        card={card}
                                                        renderContent={index <= frontCardIndex + 2}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </GestureDetector>
                                </Box>

                                <Box pb={'s16'} alignItems={'center'} zIndex={-1}>
                                    <TouchableBox onPress={onSkip}>
                                        <Text variant={'titleMedium'} color={ds.colorToken.text.primary}>
                                            {data?.skip_button}
                                        </Text>
                                    </TouchableBox>
                                    <Text
                                        variant={'bodySmall'}
                                        color={ds.colorToken.text.primary}
                                        opacity={0.7}
                                        letterSpacing={-0.08}
                                    >
                                        {data?.skip_button_info}
                                    </Text>
                                </Box>
                            </Animated.View>
                        ) : (
                            <Box flex={1}>
                                <Animated.View
                                    entering={isIos ? whatsNewEnteringKeyframe : undefined}
                                    style={styles.completeContainer}
                                >
                                    {data?.complete_image.url ? (
                                        <Image
                                            source={{ uri: data?.complete_image.url }}
                                            resizeMode={'cover'}
                                            style={styles.image}
                                        />
                                    ) : null}
                                    <Text variant={'headlineLarge'} color={ds.colorToken.text.primary}>
                                        {data?.complete_message}
                                    </Text>
                                </Animated.View>
                                <Animated.View entering={isIos ? whatsNewEnteringKeyframe : undefined}>
                                    <Button
                                        hierarchy={'primary'}
                                        textStyle={styles.buttonText}
                                        style={styles.button}
                                        label={'Back to lobby'}
                                        onPress={() => navigation.goBack()}
                                    />
                                </Animated.View>
                            </Box>
                        )}
                    </Box>
                </MaxWidthWrapper>
            </SafeAreaView>
        </Box>
    );
};

const styles = StyleSheet.create({
    close: {
        backgroundColor: ds.colorValue.background.transparent.subtle,
        borderColor: ds.colorValue.border.strong,
        borderWidth: 1,
        borderRadius: 100,
        padding: 11,
    },
    button: {
        paddingVertical: 10,
        borderRadius: 12,
        marginBottom: 16,
    },
    image: {
        width: 120,
        height: 120,
        marginBottom: -8,
    },
    completeContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    cardsLeftText: {
        ...textVariants.titleLarge,
        letterSpacing: -0.43,
        color: ds.colorValue.text.primary,
    },
    buttonText: {
        color: ds.colorValue.text.brandoninverse,
    },
});
